import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ICONS } from "../../icons";
import { useTranslation } from "../../context/I18nContext";

// HeaderSearch — the global "search pages & features" box in the header
// (next to the system name), so a user can jump straight to a page instead
// of hunting through the (now fairly deep) sidebar tree. Deliberately
// searches ONLY the already permission-filtered sidebar menu (see `items`,
// built by MainLayout from its own visibleMenuItems) — never live data
// records (customers/orders/products/...), which would need its own
// backend search endpoint(s) across every entity type. See this feature's
// own discussion: scope was kept to navigation on purpose.
const MAX_RESULTS = 8;

export default function HeaderSearch({ items }) {
  const { t } = useTranslation("header");
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Plain case-insensitive substring match against the item's own
  // (already-translated) label and its group breadcrumb (e.g. typing
  // "طباعة" also surfaces everything under the "Printing" group, not just
  // an item literally named that). No fuzzy/scoring logic — the list is
  // small (a few dozen pages at most), so a simple filter is instant and
  // predictable.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return items
      .filter(
        (it) =>
          it.label.toLowerCase().includes(q) ||
          (it.group && it.group.toLowerCase().includes(q))
      )
      .slice(0, MAX_RESULTS);
  }, [items, query]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const goTo = (path) => {
    navigate(path);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = results[activeIndex] ?? results[0];
      if (pick) goTo(pick.path);
    }
  };

  const showDropdown = open && query.trim() !== "";

  return (
    <div className="relative w-full max-w-xs" ref={containerRef}>
      <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2 focus-within:border-[var(--primary-color)] focus-within:ring-2 focus-within:ring-[var(--primary-color)]/10 transition-colors">
        <ICONS.Search className="w-4 h-4 text-gray-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t("search_placeholder", "Search pages & features...")}
          // header-search-input: MainLayout.css's own global
          // `*:focus-visible` rule (a deliberate site-wide "every focused
          // control gets a themed outline" convention) would otherwise draw
          // that outline around this small <input> itself — the "inner
          // frame" — instead of the rounded container around it. That class
          // scopes the rule off just for this input, so the container's own
          // focus-within highlight above is the only thing that lights up.
          className="header-search-input w-full bg-transparent border-none outline-none text-sm text-gray-700 placeholder:text-gray-400"
        />
      </div>

      {showDropdown && (
        <div className="absolute start-0 mt-2 w-full min-w-[16rem] bg-white rounded-2xl border border-gray-100 shadow-xl z-[10000] overflow-hidden">
          {results.length === 0 ? (
            <p className="text-center text-gray-400 text-xs italic py-6 px-4">
              {t("search_no_results", "No matching pages")}
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {results.map((r, i) => {
                const Icon = r.icon;
                return (
                  <li key={r.path}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => goTo(r.path)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-start transition-colors ${
                        i === activeIndex ? "bg-gray-50" : ""
                      }`}
                    >
                      <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center shrink-0 text-[var(--primary-color)]">
                        {Icon && <Icon className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-gray-800 truncate">{r.label}</div>
                        {r.group && (
                          <div className="text-[10px] text-gray-400 truncate">{r.group}</div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
